import React, { useRef } from 'react';
import classNames from 'classnames';
import { Map } from 'immutable';
import { Route, Switch } from 'react-router';
import { useSelector } from 'react-redux';

import {
  I18nProvider,
  EntityMapperContext,
} from '@uncinc/react-drupal-core';
import {
  KitchenSinkComponentMapperContext,
  SkipNav,
  ViewportContext,
} from '@uncinc/uncinc-react-kitchen-sink';

import blocksMapper from '../../mappers/blocks';
import DesignSystem from '../DesignSystem';
import nodeMapper from '../../mappers/node';
import paragraphMapper from '../../mappers/paragraphs';
import profileMapper from '../../mappers/profile';
import taxonomyMapper from '../../mappers/taxonomy';

import i18next from '../../config/i18next';
import Entity from '../Entity';
import CustomRoute from '../CustomRoute';
import Search from '../Search';
import HeaderMenu from '../../implementations/Menu/HeaderMenu';
import FooterMenu from '../../implementations/Menu/FooterMenu';
import {
  IS_CLIENT,
  MAIN_CONTENT_ID,
  SCREEN_LG,
  SCREEN_MD,
  SCREEN_SM,
  SCREEN_XL,
  SCREEN_XS,
} from '../../config/constants';

import {
  ButtonWrapper,
  Cookie,
  NewsletterSubscribe,
  WysiwygField,
} from '../../components';

import './style.scss';

const entityTypeMapper = {
  block: blocksMapper,
  node: nodeMapper,
  paragraph: paragraphMapper,
  profile: profileMapper,
  taxonomy: taxonomyMapper,
};

const kitchenSinkComponentMapper = {
  Button: ButtonWrapper,
  WysiwygField,
};

const viewportContextMapper = {
  screenSizes: {
    'SCREEN_MOBILE': SCREEN_XS,
    'SCREEN_TABLET': SCREEN_SM,
    'SCREEN_NORMAL': SCREEN_MD,
    'SCREEN_WIDE': SCREEN_LG,
    'SCREEN_HUGE': SCREEN_XL,
  },
};

const App = () => {
  const entityId = useSelector((state) => state.getIn(['entity', 'entityId'], null));
  const entityType = useSelector((state) => state.getIn(['entity', 'entityType'], null));
  const childEntity = useSelector((state) => state.getIn([entityType, entityId], Map()));
  const rootClasses = useSelector((state) => state.getIn(['ui', 'rootClasses']));
  const language = useSelector((state) => state.getIn(['ui', 'language']));
  const entityExists = useSelector((state) => state.getIn(['entity', 'exists']));
  const path = useSelector((state) => state.getIn(['router', 'location', 'pathname']));

  const searchInputRef = useRef(null);

  const classes = classNames('App', rootClasses.join(' '), {
    'is-front-page': childEntity.get('is_front'),
    'not-found': !entityExists,
  }, 'route-' + path.toLowerCase().replace('/', '-').replace(' ', '_'));

  return (
    <EntityMapperContext.Provider value={entityTypeMapper}>
      <I18nProvider locale={language} i18next={i18next}>
        <KitchenSinkComponentMapperContext.Provider value={kitchenSinkComponentMapper}>
          <ViewportContext.Provider value={viewportContextMapper}>
            <div className={classes} language={language}>
              {IS_CLIENT && (
                <Cookie />
              )}
              <SkipNav
                searchRef={searchInputRef}
              />
              <header>
                <HeaderMenu searchInputRef={searchInputRef} />
              </header>
              <main className="main" id={MAIN_CONTENT_ID} tabIndex={-1}>
                <Switch>
                  {/* Add routes here */}
                  <CustomRoute path={['/zoeken', '/search', '/nl/zoeken', '/en/search']} render={() => (<Search />)} />
                  <CustomRoute path={['/design-system', '/nl/design-system', '/en/design-system']} render={() => (<DesignSystem />)} />
                  <Route render={() => (<Entity />)} />
                </Switch>
              </main>
              <footer>
                <NewsletterSubscribe />
                <FooterMenu />
              </footer>
              <div id="modal-root" />
            </div>
          </ViewportContext.Provider>
        </KitchenSinkComponentMapperContext.Provider>
      </I18nProvider>
    </EntityMapperContext.Provider>
  );
};

export default App;
